import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const StatsSection = () => {
  const stats = [
    {
      number: '1000+',
      label: 'Compliance Webinars',
      icon: 'Video',
      color: 'from-emerald-500 to-green-600'
    },
    {
      number: '75K+',
      label: 'Certified',
      icon: 'Users',
      color: 'from-green-500 to-emerald-600'
    },
    {
      number: '95%',
      label: 'Completion Rate',
      icon: 'TrendingUp',
      color: 'from-emerald-600 to-green-700'
    },
    {
      number: '24/7',
      label: 'On-Demand Access',
      icon: 'Clock',
      color: 'from-green-600 to-emerald-700'
    }
  ];

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={{
        hidden: { opacity: 0 },
        visible: { 
          opacity: 1,
          transition: { staggerChildren: 0.2 }
        }
      }}
      className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-white via-emerald-50/40 to-green-50/60 relative overflow-hidden"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-60 h-60 bg-green-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-emerald-300/15 rounded-full blur-2xl" />
        <div className="absolute top-2/3 left-1/3 w-32 h-32 bg-green-300/10 rounded-full blur-2xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 40 },
            visible: { 
              opacity: 1, 
              y: 0,
              transition: { type: "spring", stiffness: 100, damping: 15 }
            }
          }}
          className="text-center mb-16 md:mb-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-100 to-green-100 rounded-full border border-emerald-200 shadow-sm mb-8"
          >
            <Icon name="BarChart" size={18} className="text-green-700" />
            <span className="font-semibold text-green-800 tracking-wide">Our Impact in Numbers</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
          >
            <span className="bg-gradient-to-r from-green-800 via-emerald-700 to-green-800 bg-clip-text text-transparent">
              Leading the Compliance
            </span>
            <br />
            <span className="bg-gradient-to-r from-emerald-700 via-green-700 to-emerald-700 bg-clip-text text-transparent">
              Training Industry
            </span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-2"
          >
            Delivering expert-led compliance training that keeps organizations ahead of regulatory requirements.
          </motion.p>
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Join thousands of professionals who trust ComplianceTrained for their compliance needs.
          </p>
        </motion.div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              variants={{
                hidden: { opacity: 0, y: 60, scale: 0.95 },
                visible: { 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  transition: { 
                    type: "spring", 
                    stiffness: 100,
                    damping: 15,
                    delay: index * 0.15 
                  }
                }
              }}
              whileHover={{ 
                y: -15,
                scale: 1.05,
                transition: { type: "spring", stiffness: 400, damping: 10 }
              }}
              className="relative group"
            >
              {/* Card Container */}
              <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:shadow-emerald-500/20 transition-all duration-500 border-2 border-transparent hover:border-emerald-300/50 overflow-hidden h-full">
                {/* Animated Background Gradient */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(34, 197, 94, 0.08) 100%)`
                  }}
                />
                
                {/* Icon Container */}
                <motion.div 
                  className={`relative w-20 h-20 mx-auto mb-8 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-all duration-500`}
                  animate={{ 
                    boxShadow: [
                      '0 15px 30px rgba(16, 185, 129, 0.25)',
                      '0 20px 40px rgba(16, 185, 129, 0.4)',
                      '0 15px 30px rgba(16, 185, 129, 0.25)'
                    ]
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  whileHover={{
                    rotate: [0, -5, 5, 0],
                    scale: 1.15,
                    transition: { duration: 0.5 }
                  }}
                >
                  <Icon 
                    name={stat.icon} 
                    size={36} 
                    className="text-white relative z-10" 
                  />
                  
                  {/* Icon Glow Effect */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    animate={{
                      boxShadow: [
                        '0 0 25px rgba(16, 185, 129, 0.6)',
                        '0 0 45px rgba(16, 185, 129, 0.9)',
                        '0 0 25px rgba(16, 185, 129, 0.6)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
                
                {/* Number */}
                <motion.div 
                  className="relative text-5xl lg:text-6xl font-black mb-4 tracking-tight"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ 
                    type: "spring", 
                    stiffness: 200, 
                    damping: 20,
                    delay: index * 0.2 
                  }}
                >
                  <span className="bg-gradient-to-r from-green-700 via-emerald-600 to-green-700 bg-clip-text text-transparent drop-shadow-sm">
                    {stat.number}
                  </span>
                </motion.div>
                
                {/* Label */}
                <div className="relative text-sm uppercase tracking-widest text-gray-700 font-bold px-4 leading-tight mb-4">
                  {stat.label}
                </div>

                {/* Progress/Indicator Line */}
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "60%" }}
                  transition={{ 
                    duration: 1, 
                    delay: index * 0.2 + 0.3,
                    ease: "easeOut"
                  }}
                  className={`h-1 bg-gradient-to-r ${stat.color} rounded-full mx-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                />
                
                {/* Decorative Corner Elements */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-emerald-100/60 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-100/60 to-transparent rounded-tr-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              {/* Floating Badge */}
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: index * 0.2 + 0.5, duration: 0.4 }}
                className="mt-4 flex items-center justify-center"
              >
                <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-green-50 rounded-full border border-emerald-200 text-xs text-green-700 font-semibold opacity-0 group-hover:opacity-100 transition-all duration-300">
                  <Icon name="TrendingUp" size={12} className="text-green-600" />
                  <span>Industry Leading</span>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-16 lg:mt-20"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1 }}
            className="bg-gradient-to-br from-white to-emerald-50/60 rounded-3xl border-2 border-emerald-100 shadow-xl p-8 md:p-10"
          >
            <h3 className="text-xl font-bold text-center text-gray-900 mb-8">
              Trusted & Certified By Industry Leaders
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              {[
                { icon: 'Award', label: 'SHRM Certified', color: 'emerald-600' },
                { icon: 'Shield', label: 'HRCI Approved', color: 'green-600' },
                { icon: 'CheckCircle', label: 'ISO 9001 Compliant', color: 'emerald-600' }
              ].map((item, idx) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + idx * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                  className="flex flex-col items-center text-center p-6 rounded-2xl bg-white border border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br from-${item.color.replace('600', '500')} to-${item.color} rounded-2xl flex items-center justify-center mb-4 shadow-md`}>
                    <Icon name={item.icon} size={24} className="text-white" />
                  </div>
                  <span className="text-lg font-semibold text-gray-800">{item.label}</span>
                  <span className="text-sm text-gray-500 mt-2">Accredited Provider</span>
                </motion.div>
              ))}
            </div>
            
            {/* Additional Note */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              className="mt-10 pt-8 border-t border-gray-200 text-center"
            >
              <p className="text-gray-600 text-sm">
                All our programs meet the highest industry standards and are regularly audited for quality assurance
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default StatsSection;